// Diagram callout tag — the shared visual language for parts-diagram ref tags,
// per the Catalog Parts Illustration Guidelines v2.0 (July 2026).
//
// Two ideas from that spec drive everything here:
//   1. Priority is a single NAVY RAMP of descending visual weight
//      (filled → indigo-outline → gray-outline → dashed-faded), so it survives
//      grayscale and colour-vision deficiency. Colour alone is never the signal:
//      every level also differs in fill/stroke/opacity + a mark (★ / ⊘).
//   2. Status OVERLAYS priority on dimensions the ramp doesn't use — selection is
//      a lilac halo RING drawn around the tag, in-cart is a green satellite ✓
//      BADGE at the top-right. A part can be Recommended + Selected + In-cart at
//      once and all three read clearly.
//
// Used by both the part-results diagram (ImageWithCallouts, canvas) and the
// clarification diagram (ClarificationDiagramInline, which reads the tokens for
// its HTML tags) so the two never drift.

export type CalloutPriority = 'hero' | 'secondary' | 'optional' | 'na';

// ── Product-tunable thresholds (Update Notes v2.1, "Config to expose") ───────
// Deliberately named constants rather than inline numbers: both are product
// decisions that get retuned, and a magic 0.8 buried in a style branch is the
// kind of thing that drifts between the diagram and the table it mirrors.

/** At/above this probability a suggestion is CONFIDENT and wears the navy ramp.
 *  Below it the suggestion is an ESTIMATE and switches to the amber family. */
export const ESTIMATION_THRESHOLD = 0.80;

/** Probability gap (in absolute score, i.e. 0.15 = 15 pts) between the top two
 *  candidates below which the recommendation reads as AMBIGUOUS rather than
 *  confident — the "two look-alikes, we can't call it" state. */
export const AMBIGUITY_GAP = 0.15;

/** Floor of the MID confidence band. Below it a suggestion is a long shot; at or
 *  above ESTIMATION_THRESHOLD it is confident. Product-tunable, like its sibling. */
export const LOW_BAND_CEILING = 0.40;

export type ConfidenceBand = 'high' | 'mid' | 'low' | 'unscored';

/** Which confidence band a probability falls in.
 *
 *  `unscored` is NOT a fourth grade — it means the part was never ranked against
 *  the request at all, which most parts on a diagram never are. Folding those into
 *  "low" would tell the user we weighed them and found them wanting, which is a
 *  claim we cannot make, so they keep their own bucket.
 */
export function confidenceBand(
  score: number | null | undefined,
  priority?: CalloutPriority,
): ConfidenceBand {
  if (typeof score === 'number' && score > 0) {
    if (score >= ESTIMATION_THRESHOLD) return 'high';
    if (score >= LOW_BAND_CEILING) return 'mid';
    return 'low';
  }
  // No score. On a parts diagram that is the NORMAL case — only the requested
  // part and the "you may also like" set are ever scored, so a score-only rule
  // sends the very part the user searched for into the unscored bucket and the
  // panel opens on 43 bolts. Fall back to what the search DID decide:
  //   hero      = this is the part that matched the request  -> High
  //   secondary = offered as a related alternative            -> Possible
  // Everything else genuinely was never weighed and stays unscored.
  if (priority === 'hero') return 'high';
  if (priority === 'secondary') return 'mid';
  return 'unscored';
}

/** True when a probability score is low enough to render as an estimate.
 *  An ABSENT score is not an estimate: "we never scored this" and "we scored it
 *  poorly" are different claims, and amber-ing the first would tell the user we
 *  doubt a part we never ranked at all. */
export function isEstimatedScore(score: number | null | undefined): boolean {
  return typeof score === 'number' && score > 0 && score < ESTIMATION_THRESHOLD;
}

export type MatchBand = 'high' | 'medium' | 'low';

/** The user-facing grade for a model score: High / Medium / Low instead of a
 *  percentage. The scorer is a ranking, not a calibrated probability — the same
 *  candidate can read 82% on one attempt and 74% on the next — so two digits of
 *  precision promised more than the system knows and confused people. The bands
 *  reuse the product thresholds above so a "High" label and a navy (confident)
 *  tag always agree. Unscored parts get no band (null), never "Low". */
export function matchBand(score: number | null | undefined): MatchBand | null {
  const band = confidenceBand(score);
  if (band === 'high') return 'high';
  if (band === 'mid') return 'medium';
  if (band === 'low') return 'low';
  return null;
}

/** i18n key + English default for a band label — one place so the question
 *  rows, the results table and the diagram popup never drift. */
export const MATCH_BAND_LABEL: Record<MatchBand, { key: string; defaultValue: string }> = {
  high: { key: 'matchBand.high', defaultValue: 'High match' },
  medium: { key: 'matchBand.medium', defaultValue: 'Medium match' },
  low: { key: 'matchBand.low', defaultValue: 'Low match' },
};

// ── Match strength — 4-level bar indicator (Match Strength Bars spec v3) ─────
// The parts-diagram viewer grades every scored row on FOUR levels, drawn as a
// signal-strength glyph + one word. Four levels (not the three bands above)
// so a fastener on the same sheet no longer reads the same as a genuine
// neighbour of the answer. Thresholds are placeholders until calibrated
// against outcomes (spec §7.1) — tune them here, nowhere else.

/** At/above → level 3 (Medium). Below → Low / Very low. */
export const MATCH_MEDIUM_FLOOR = 0.60;

/** 4 High · 3 Medium · 2 Low · 1 Very low. `null` = no estimate (never 1 bar). */
export type MatchLevel = 1 | 2 | 3 | 4;

export function matchLevel(score: number | null | undefined): MatchLevel | null {
  if (typeof score !== 'number' || !Number.isFinite(score) || score <= 0) return null;
  if (score >= ESTIMATION_THRESHOLD) return 4;
  if (score >= MATCH_MEDIUM_FLOOR) return 3;
  if (score >= LOW_BAND_CEILING) return 2;
  return 1;
}

/** Colour tokens (spec §6). Bars use the tier colour, the word uses the AA
 *  text colour beside it — amber bars are under 3:1 on white, so the meaning
 *  never rests on the bars alone. Mirrored as CSS custom properties in
 *  MatchStrength.css. */
export const MATCH_COLOR = {
  high: '#6C5CE7', highFg: '#4A3BC9',
  medium: '#F59E0B', mediumFg: '#8A5A00',
  low: '#8E8BA3', lowFg: '#5B5872',
  vlow: '#B9B7C6', vlowFg: '#696682',
  noneFg: '#5F5C57',
  track: '#E4E2EA', trackOnTint: '#D8D5E6',
} as const;

export const MATCH_LEVEL_STYLE: Record<MatchLevel, { bar: string; fg: string }> = {
  4: { bar: MATCH_COLOR.high, fg: MATCH_COLOR.highFg },
  3: { bar: MATCH_COLOR.medium, fg: MATCH_COLOR.mediumFg },
  2: { bar: MATCH_COLOR.low, fg: MATCH_COLOR.lowFg },
  1: { bar: MATCH_COLOR.vlow, fg: MATCH_COLOR.vlowFg },
};

/** i18n key + English default per level; `none` is the "No estimate" label. */
export const MATCH_LEVEL_LABEL: Record<MatchLevel | 'none', { key: string; defaultValue: string }> = {
  4: { key: 'matchLevel.high', defaultValue: 'High' },
  3: { key: 'matchLevel.medium', defaultValue: 'Medium' },
  2: { key: 'matchLevel.low', defaultValue: 'Low' },
  1: { key: 'matchLevel.veryLow', defaultValue: 'Very low' },
  none: { key: 'matchLevel.none', defaultValue: 'No estimate' },
};

/** Diagram tag fill for a scored, non-answer part — the same tier colour the
 *  list's bars use, so list and drawing read as one scale (spec §4):
 *  High filled purple · Medium amber wash · Low grey outline · Very low dashed. */
export function matchTagStyle(level: MatchLevel): { fill: string; stroke: string; fg: string; strokeWidth: number; dash?: string } {
  switch (level) {
    case 4: return { fill: MATCH_COLOR.high, stroke: MATCH_COLOR.high, fg: '#FFFFFF', strokeWidth: 1.5 };
    case 3: return { fill: DIAGRAM_TAG_EST_BG, stroke: MATCH_COLOR.medium, fg: MATCH_COLOR.mediumFg, strokeWidth: 1.5 };
    case 2: return { fill: '#FFFFFF', stroke: MATCH_COLOR.low, fg: MATCH_COLOR.lowFg, strokeWidth: 1.5 };
    default: return { fill: '#FFFFFF', stroke: MATCH_COLOR.vlow, fg: MATCH_COLOR.vlowFg, strokeWidth: 1.25, dash: '3 2' };
  }
}
const DIAGRAM_TAG_EST_BG = '#FEF3C7';

/** True when the top two candidates are close enough that neither can be
 *  presented as the answer (drives the ambiguous/"select one" copy). */
export function isAmbiguousPair(
  top: number | null | undefined,
  runnerUp: number | null | undefined,
): boolean {
  if (typeof top !== 'number' || typeof runnerUp !== 'number') return false;
  return top - runnerUp < AMBIGUITY_GAP;
}

// The only palette values beyond the v1.0 design tokens (see spec §3).
export const DIAGRAM_TAG = {
  heroBg: '#1E0DB8', // 1 · Recommended — brand navy fill
  secStroke: '#3627BF', // 2 · Secondary — indigo outline
  secFg: '#3627BF',
  optStroke: '#999999', // 3 · Optional — muted solid outline, full opacity
  optFg: '#696682',
  naFill: '#F6F4F0', // 4 · Not available — subtle fill + dashed + slash + 45%
  naStroke: '#C9C6C0',
  naFg: '#B0ADA6',
  haloFill: 'rgba(219,216,255,0.55)', // selection ring
  haloStroke: '#1E0DB8',
  cart: '#137F67', // the ONLY green on the screen = in cart
  white: '#FFFFFF',
  // Estimate (<ESTIMATION_THRESHOLD) — the amber family, shared with the
  // ambiguity alert so "we're not sure" reads as ONE colour across the viewer.
  // It replaces the navy ramp fill/stroke/text on the tag; it never replaces the
  // SELECTION overlay (halo stays navy/lilac) or the in-cart badge (stays green),
  // because those are status, not confidence.
  estBg: '#FEF3C7',
  estStroke: '#F59E0B',
  estFg: '#92610A',
} as const;

// CATA AI Design v1.0 tokens used by the RESULTS PANEL / parts-list chrome that
// surrounds the tags (Catalog Parts Illustration Guidelines §2–§5). Kept here so
// the diagram tags and the table they mirror never drift.
export const DIAGRAM_PALETTE = {
  bgPage: '#FAF9F6',
  bgSurface: '#FFFFFF',
  bgSubtle: '#F6F4F0',
  textPrimary: '#201E1A',
  textMuted: '#999999',
  border: 'rgba(32, 30, 26, 0.12)',
  backdrop: 'rgba(32, 30, 26, 0.5)',
  navy: '#1E0DB8', // = brand navy (priority 1 / hero)
  indigo: '#3627BF', // = brand indigo (priority 2 / secondary)
  lilac: '#DBD8FF', // selection halo / "Seleccionado" chip bg
  selBg: 'rgba(219, 216, 255, 0.35)', // selected table-row background
  tagBg: '#E5E2FF', // TRAD / request-counter chip bg
  tagFg: '#3627BF',
  neutralBg: '#E0DEED', // position/trim chip bg
  neutralFg: '#696682',
  cart: '#137F67', // in-cart green (reserved)
  warningBg: '#FEF3C7', // "Corregido" amber
  warningFg: '#92610A',
  naPill: '#C9C6C0',
  // Functionality tag on a part card: what the customer asked for / not.
  successBg: '#C7FFE8',
  successFg: '#0F6B57',
  dangerBg: '#FDE3E1',
  dangerFg: '#A6301F',
} as const;

const FONT = 'Inter Tight, system-ui, -apple-system, sans-serif';

export interface DiagramTagOpts {
  /** Ref number shown inside the circle (the callout label). */
  number: string;
  priority: CalloutPriority;
  /** Rank-1 hero gets the ★ (top-left inside the filled circle). Ignored when
   *  `estimated` — the star claims "this is THE one", which is exactly the claim
   *  a sub-threshold score cannot support (v2.1 §3). */
  star?: boolean;
  /** Below ESTIMATION_THRESHOLD: render in the amber estimate family instead of
   *  the navy priority ramp. Overrides hero/secondary/optional fill, stroke and
   *  text; `na` still wins (an unavailable part is not an estimate). */
  estimated?: boolean;
  /** The estimate behind `estimated`. Shades the amber fill: a low score draws
   *  pale, one near ESTIMATION_THRESHOLD draws deep, so the diagram itself ranks
   *  the candidates at a glance. Omitted = flat estBg. */
  score?: number;
  /** Selection halo ring drawn AROUND the priority tag. */
  selected?: boolean;
  /** Draw the navy "SELECTED" pill under the tag (v2.1 §4). Set only for the
   *  ONE currently-selected tag — the halo alone reads as emphasis, so the pill
   *  is what actually names the state. */
  selectedPill?: boolean;
  /** Localized text for that pill; the util has no i18n of its own. */
  selectedLabel?: string;
  /** Green satellite ✓ badge at the tag's top-right. */
  inCart?: boolean;
  /** The USER chose this part when answering a clarification (result row
   *  `user_selected`). Draws a green "MANUAL SELECTION" pill under the tag so the
   *  diagram says a person picked it, not the resolver. Unlike `selectedPill`
   *  this is a property of the part, not a transient viewer state, so it is
   *  drawn whether or not the tag is currently selected. */
  userPick?: boolean;
  /** Localized text for that pill; the util has no i18n of its own. */
  userPickLabel?: string;
  /** px multiplier: 1 at native image resolution, 1/zoom inside a zoomed modal
   *  so the tag stays a constant on-screen size. */
  scale?: number;
  /** Anchor the tag was displaced from (see resolveTagOverlaps). When given and
   *  far enough away, a thin leader line is drawn back to it so the ref still
   *  visibly belongs to its part. */
  leaderTo?: { x: number; y: number } | null;
}

// Approx advance width of one bold Inter Tight glyph at the 11px tag font. Ref
// codes are alphanumeric and near-monospace at this weight, so a per-char
// estimate is accurate enough to size the tag — and being purely geometric it
// gives the canvas and the SVG clarification tag IDENTICAL widths with no
// measureText (which SVG can't do), so the two surfaces never drift.
const TAG_CHAR_W = 7.2;

/** Half-height (radius `r`) and half-width of a ref tag. Short labels stay a
 *  circle (halfW === r); longer ones grow into a horizontal stadium/pill so the
 *  ref never overflows the tag. Shared by both diagram surfaces. */
export function diagramTagMetrics(
  label: string,
  priority: CalloutPriority,
  scale = 1,
): { r: number; halfW: number } {
  const r = (priority === 'hero' ? 14 : 13) * scale;
  const textHalf = ((label ? label.length : 0) * TAG_CHAR_W) / 2 * scale;
  const pad = 5 * scale; // breathing room between the ref and the rounded ends
  return { r, halfW: Math.max(r, textHalf + pad) };
}

// ── Overlap resolution ───────────────────────────────────────────────────────
// Catalog diagrams routinely put two callouts at (nearly) the same spot — two
// halves of one assembly, or left/right variants sharing a location. Drawn at
// their anchors the tags cover each other and the ref underneath is unreadable
// (e.g. "71504E" swallowed by "71560" sitting on top of it). Long refs make it
// worse: they grow into wide stadiums, so tags that once cleared each other as
// circles now collide sideways.
//
// Tags are nudged, never dropped: every ref stays on the diagram. The first tag
// in the list keeps its exact anchor, so callers should pass them in priority
// order (hero first) — the most important tag never moves.

export interface TagAnchor {
  /** Ref label — drives the tag width via diagramTagMetrics. */
  label: string;
  /** The tag's ideal center (the callout's own position). */
  cx: number;
  cy: number;
  priority: CalloutPriority;
  /** Extents of the catalog's own hotspot box around the printed ref, in the
   *  same units as cx/cy. Other tags cannot be displaced onto this box;
   *  the diagram renderer replaces its printed text before drawing new tags. */
  boxW?: number;
  boxH?: number;
}

export interface ResolvedTagPos {
  cx: number;
  cy: number;
  /** Half-extents of the placed tag — the box to hit-test against. */
  halfW: number;
  halfH: number;
  /** True when the tag had to be displaced from its anchor. */
  moved: boolean;
  /** px multiplier the tag was sized with — pass it straight to drawDiagramTag
   *  so the drawn pill matches the box that was placed. */
  scale: number;
}

/** Gap kept between two neighbouring tags, in unscaled px. */
const TAG_GAP = 3;
/** How far out the search goes before giving up and stacking at the anchor. */
const MAX_RINGS = 12;

function boxesOverlap(
  a: { cx: number; cy: number; halfW: number; halfH: number },
  b: { cx: number; cy: number; halfW: number; halfH: number },
  gap: number,
): boolean {
  // Stadiums are approximated by their bounding boxes: cheap, and erring toward
  // "overlapping" only ever buys a little extra breathing room.
  return (
    Math.abs(a.cx - b.cx) < a.halfW + b.halfW + gap &&
    Math.abs(a.cy - b.cy) < a.halfH + b.halfH + gap
  );
}

/** Displacement candidates, nearest first. Vertical is tried before horizontal
 *  because ref tags are wider than they are tall — a small vertical nudge clears
 *  a collision that would need a much larger sideways move. */
function offsetCandidates(halfW: number, halfH: number, gap: number): Array<[number, number]> {
  const out: Array<[number, number]> = [[0, 0]];
  for (let k = 1; k <= MAX_RINGS; k++) {
    // One step clears a same-sized neighbour outright, so k is usually 1.
    const dy = k * (2 * halfH + gap);
    const dx = k * (halfW + gap);
    out.push(
      [0, -dy], [0, dy],
      [dx, -dy], [-dx, -dy],
      [dx, dy], [-dx, dy],
      [dx, 0], [-dx, 0],
    );
  }
  return out;
}

/**
 * Place ref tags so none covers another, moving each as little as possible.
 *
 * Returns one entry per input anchor, in input order. Pass anchors in priority
 * order: earlier entries are placed first and keep their anchor, later ones give
 * way. A tag that cannot be fitted anywhere within MAX_RINGS stays at its anchor
 * rather than being flung across the diagram — a rare overlap beats a ref that
 * no longer points at its part.
 */
export function resolveTagOverlaps(
  anchors: TagAnchor[],
  scale = 1,
  gap = TAG_GAP,
): ResolvedTagPos[] {
  const placed: Array<{ cx: number; cy: number; halfW: number; halfH: number }> = [];
  // Every OTHER callout's printed ref is ground a displaced tag must not cover:
  // a tag pushed off its own ref onto a neighbour's misreads worse than two
  // tags touching. Only anchors that carry a hotspot box take part.
  const refBoxes = anchors.map(a =>
    a.boxW && a.boxH && a.boxW > 0 && a.boxH > 0
      ? { cx: a.cx, cy: a.cy, halfW: a.boxW / 2, halfH: a.boxH / 2 }
      : null,
  );
  return anchors.map((a, i) => {
    const tagScale = scale; // preserve readable on-screen text at every zoom
    const g = gap * tagScale;
    const { r, halfW } = diagramTagMetrics(a.label, a.priority, tagScale);
    let best: ResolvedTagPos | null = null;
    for (const [dx, dy] of offsetCandidates(halfW, r, g)) {
      const box = { cx: a.cx + dx, cy: a.cy + dy, halfW, halfH: r };
      const moved = dx !== 0 || dy !== 0;
      // At its own anchor a tag may TOUCH a neighbour — refs printed edge to
      // edge (RH/LH pairs) are worth more in place than pushed a row away. The
      // breathing gap applies only once a tag has to be displaced anyway.
      if (placed.some(p => boxesOverlap(box, p, moved ? g : 0))) continue;
      if (moved && refBoxes.some((rb, j) => j !== i && rb !== null && boxesOverlap(box, rb, 0))) continue;
      best = { ...box, moved, scale: tagScale };
      break;
    }
    const pos = best ?? { cx: a.cx, cy: a.cy, halfW, halfH: r, moved: false, scale: tagScale };
    placed.push({ cx: pos.cx, cy: pos.cy, halfW: pos.halfW, halfH: pos.halfH });
    return pos;
  });
}

/** Trace a horizontal stadium (rounded-rect with semicircular ends) of half-width
 *  `halfW` and half-height `halfH`, centered at (cx, cy). When halfW === halfH it
 *  degenerates to a circle, so short tags render exactly as before. */
function stadiumPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
): void {
  const straight = Math.max(0, halfW - halfH); // half-length of the flat top/bottom
  ctx.beginPath();
  ctx.moveTo(cx - straight, cy - halfH);
  ctx.lineTo(cx + straight, cy - halfH);
  ctx.arc(cx + straight, cy, halfH, -Math.PI / 2, Math.PI / 2); // right cap
  ctx.lineTo(cx - straight, cy + halfH);
  ctx.arc(cx - straight, cy, halfH, Math.PI / 2, -Math.PI / 2); // left cap
  ctx.closePath();
}

/** Draw one diagram ref tag centered at (cx, cy) on a 2D canvas. Fully self-
 *  contained (save/restore), so it never leaks font/alpha/dash state to callers. */
const hexToRgb = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
const mixHex = (a: string, b: string, t: number) => {
  const [ar, ag, ab] = hexToRgb(a); const [br, bg, bb] = hexToRgb(b);
  return `rgb(${Math.round(ar + (br - ar) * t)}, ${Math.round(ag + (bg - ag) * t)}, ${Math.round(ab + (bb - ab) * t)})`;
};

/** Colours for an estimated tag, graded by score: near 0 it looks like a plain
 *  optional (grey) tag, at ESTIMATION_THRESHOLD it is fully amber — so on a sheet
 *  where every background part carries a low fallback estimate, only the real
 *  candidates light up. No score = flat amber. */
export function estimateStyle(score: number | null | undefined): { fill: string; stroke: string; fg: string } {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return { fill: DIAGRAM_TAG.estBg, stroke: DIAGRAM_TAG.estStroke, fg: DIAGRAM_TAG.estFg };
  }
  const t = Math.min(1, Math.max(0, score / ESTIMATION_THRESHOLD));
  return {
    fill: mixHex(DIAGRAM_TAG.white, DIAGRAM_TAG.estStroke, 0.55 * t),
    stroke: mixHex(DIAGRAM_TAG.optStroke, DIAGRAM_TAG.estStroke, t),
    fg: mixHex(DIAGRAM_TAG.optFg, DIAGRAM_TAG.estFg, t),
  };
}

export function drawDiagramTag(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  opts: DiagramTagOpts,
): void {
  const {
    number, priority, star, selected, inCart, scale = 1, leaderTo,
    estimated, score, selectedPill, selectedLabel = 'SELECTED',
    userPick, userPickLabel = 'MANUAL SELECTION',
  } = opts;
  const na = priority === 'na';
  // An unavailable part is not an "estimate" — it is absent. `na` keeps its own
  // dashed/slashed treatment and never turns amber.
  const est = !!estimated && !na;
  const estStyle = estimateStyle(score);
  const { r, halfW } = diagramTagMetrics(number, priority, scale);

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Not-available parts recede to 45% (decorative de-emphasis; the info is
  // duplicated at full contrast in the panel note).
  if (na) ctx.globalAlpha = 1;

  // ── Leader line back to the anchor, for a tag moved off its part to avoid an
  //    overlap. Drawn first so the tag covers its near end. Skipped when the tag
  //    still sits over its anchor (the line would just be a smudge under it).
  if (leaderTo) {
    const dx = leaderTo.x - cx;
    const dy = leaderTo.y - cy;
    if (Math.abs(dx) > halfW || Math.abs(dy) > r) {
      ctx.save();
      ctx.globalAlpha = (na ? 0.45 : 1) * 0.55;
      ctx.strokeStyle = priority === 'hero' ? DIAGRAM_TAG.heroBg : DIAGRAM_TAG.optStroke;
      ctx.lineWidth = 1 * scale;
      ctx.setLineDash([3 * scale, 2 * scale]);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(leaderTo.x, leaderTo.y);
      ctx.stroke();
      // A small dot pins the line to the spot the ref actually refers to.
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(leaderTo.x, leaderTo.y, 1.8 * scale, 0, Math.PI * 2);
      ctx.fillStyle = priority === 'hero' ? DIAGRAM_TAG.heroBg : DIAGRAM_TAG.optStroke;
      ctx.fill();
      ctx.restore();
    }
  }

  // ── Selection halo — a lilac ring drawn behind so the priority tag stays legible.
  if (selected) {
    stadiumPath(ctx, cx, cy, halfW + 7 * scale, r + 7 * scale);
    ctx.fillStyle = DIAGRAM_TAG.haloFill;
    ctx.fill();
    ctx.lineWidth = 2 * scale;
    ctx.strokeStyle = DIAGRAM_TAG.haloStroke;
    ctx.stroke();
  }

  // ── Priority pill (a circle for short refs, a stadium for long ones).
  stadiumPath(ctx, cx, cy, halfW, r);
  if (est) {
    // Amber estimate — one flat treatment for every priority level, because the
    // point of the state is that the ranking is NOT trustworthy enough to grade.
    ctx.fillStyle = estStyle.fill;
    ctx.fill();
    ctx.lineWidth = 1.5 * scale;
    ctx.strokeStyle = estStyle.stroke;
    ctx.stroke();
  } else if (priority === 'hero') {
    ctx.fillStyle = DIAGRAM_TAG.heroBg;
    ctx.fill();
  } else if (na) {
    ctx.fillStyle = DIAGRAM_TAG.naFill;
    ctx.fill();
    ctx.setLineDash([3 * scale, 2 * scale]);
    ctx.lineWidth = 1 * scale;
    ctx.strokeStyle = DIAGRAM_TAG.naStroke;
    ctx.stroke();
    ctx.setLineDash([]);
  } else {
    ctx.fillStyle = DIAGRAM_TAG.white;
    ctx.fill();
    ctx.lineWidth = 1.5 * scale;
    ctx.strokeStyle = priority === 'secondary' ? DIAGRAM_TAG.secStroke : DIAGRAM_TAG.optStroke;
    ctx.stroke();
  }

  // ── Ref number.
  const fg = est
    ? estStyle.fg
    : priority === 'hero' ? DIAGRAM_TAG.white
      : priority === 'secondary' ? DIAGRAM_TAG.secFg
        : na ? DIAGRAM_TAG.naFg : DIAGRAM_TAG.optFg;
  ctx.fillStyle = fg;
  ctx.font = `bold ${12 * scale}px ${FONT}`;
  ctx.fillText(number, cx, cy + 0.5 * scale);

  // ── Rank-1 star — small, tucked top-left just inside the filled hero pill.
  if (star && priority === 'hero' && !est) {
    ctx.font = `${8 * scale}px ${FONT}`;
    ctx.fillText('★', cx - halfW + 6 * scale, cy - 4.5 * scale);
  }

  // ── Not-available diagonal slash (⊘), spanning the pill.
  if (na) {
    ctx.beginPath();
    ctx.moveTo(cx - halfW + 4 * scale, cy + r - 4 * scale);
    ctx.lineTo(cx + halfW - 4 * scale, cy - r + 4 * scale);
    ctx.strokeStyle = DIAGRAM_TAG.naFg;
    ctx.lineWidth = 1.4 * scale;
    ctx.lineCap = 'round';
    ctx.stroke();
  }

  // ── In-cart satellite badge — green ✓ at top-right, ringed in white so it
  //    stays legible over the navy fill / lilac halo. Full opacity always.
  if (inCart) {
    ctx.globalAlpha = 1;
    const bx = cx + halfW - 3 * scale;
    const by = cy - 11 * scale;
    const br = 8.5 * scale;
    ctx.beginPath();
    ctx.arc(bx, by, br + 2 * scale, 0, Math.PI * 2);
    ctx.fillStyle = DIAGRAM_TAG.white;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(bx, by, br, 0, Math.PI * 2);
    ctx.fillStyle = DIAGRAM_TAG.cart;
    ctx.fill();
    ctx.strokeStyle = DIAGRAM_TAG.white;
    ctx.lineWidth = 1.8 * scale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(bx - 3.5 * scale, by);
    ctx.lineTo(bx - 1 * scale, by + 2.6 * scale);
    ctx.lineTo(bx + 3.6 * scale, by - 2.6 * scale);
    ctx.stroke();
  }

  // ── "SELECTED" pill, ~25px below the tag center (v2.1 §4). The halo says
  //    "something is emphasised here"; the pill is what says WHICH state that is,
  //    which matters once amber estimate tags are on screen and colour alone can
  //    no longer carry it. Navy/lilac always — selection is status, not
  //    confidence, so it does NOT inherit the amber estimate family.
  //    A user-picked tag puts its own green pill in this slot first (below);
  //    the SELECTED pill then stacks one row further down so both stay readable.
  let pillY = cy + 25 * scale;
  const ph = 18 * scale;

  // ── "MANUAL SELECTION" pill — the user chose this part in a clarification.
  //    Green, the confirmation colour (same family as the in-cart badge and the
  //    result card's "Manual Selection" chip), with a ✓ so it does not rely on
  //    colour alone. Drawn whenever the part is a user pick, selected or not.
  if (userPick) {
    ctx.globalAlpha = 1;
    const text = userPickLabel.toUpperCase();
    ctx.font = `bold ${8.5 * scale}px ${FONT}`;
    const textW = ctx.measureText(text).width;
    const checkW = 9 * scale;
    const pw = textW + checkW + 16 * scale;
    stadiumPath(ctx, cx, pillY, pw / 2, ph / 2);
    ctx.fillStyle = DIAGRAM_TAG.cart;
    ctx.fill();
    // ✓ then the label, laid out from the pill's left edge.
    const left = cx - pw / 2 + 8 * scale;
    ctx.strokeStyle = DIAGRAM_TAG.white;
    ctx.lineWidth = 1.6 * scale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(left, pillY);
    ctx.lineTo(left + 2.4 * scale, pillY + 2.4 * scale);
    ctx.lineTo(left + 6.5 * scale, pillY - 2.8 * scale);
    ctx.stroke();
    ctx.fillStyle = DIAGRAM_TAG.white;
    ctx.textAlign = 'left';
    ctx.fillText(text, left + checkW, pillY + 0.5 * scale);
    ctx.textAlign = 'center';
    pillY += ph + 3 * scale;
  }

  if (selectedPill) {
    ctx.globalAlpha = 1;
    const pw = 72 * scale;
    const py = pillY;
    stadiumPath(ctx, cx, py, pw / 2, ph / 2);
    ctx.fillStyle = DIAGRAM_TAG.haloStroke; // brand navy
    ctx.fill();
    ctx.fillStyle = DIAGRAM_TAG.white;
    ctx.font = `bold ${8.5 * scale}px ${FONT}`;
    ctx.fillText(selectedLabel.toUpperCase(), cx, py + 0.5 * scale);
  }

  ctx.restore();
}
